import { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import LongevityBot from '../components/LongevityBot';
import BiometricMeter from '../components/BiometricMeter';

interface OnboardingData {
  name: string;
  gender: string;
  age: string;
  country: string;
  weightKg: number;
  heightCm: number;
  waistCm: number;
  sleepQuality: string;
  sugarIntake: string;
  cardioHealth: string;
  stressLevel: string;
  eatingWindow: string;
  socialConnection: string;
  functionalStrength: string;
}

interface Props {
  onComplete: () => void;
  onSkip?: () => void;
}

const questions = [
  {
    id: 'sleepQuality',
    title: 'The "Repair" Check',
    subtitle: 'Sleep Quality',
    question: 'How do you feel within 30 minutes of waking up?',
    mascotMessage: "Sleep is when your body does its magic repair work! Let's see how refreshed you wake up.",
    options: [
      { label: 'Groggy / Need Coffee immediately', value: 'groggy', impact: 1.5, emoji: '☕' },
      { label: 'Neutral / Okay', value: 'neutral', impact: 0, emoji: '😐' },
      { label: 'Alert / Ready to go', value: 'alert', impact: -1.0, emoji: '⚡' },
    ],
  },
  {
    id: 'sugarIntake',
    title: 'The "Glycation" Check',
    subtitle: 'Sugar Intake',
    question: 'How often do you consume sugary drinks, desserts, or processed snacks?',
    mascotMessage: "Sugar can speed up aging, but don't worry! Knowledge is power, and we're here to help you improve!",
    options: [
      { label: 'Daily', value: 'daily', impact: 2.0, emoji: '🍰' },
      { label: '2-3 times a week', value: 'sometimes', impact: 0.5, emoji: '🍪' },
      { label: 'Rarely / Never', value: 'rarely', impact: -1.5, emoji: '🥗' },
    ],
  },
  {
    id: 'cardioHealth',
    title: 'The "VO2 Max" Proxy',
    subtitle: 'Cardio Health',
    question: 'Can you climb two flights of stairs briskly without stopping?',
    mascotMessage: "Your heart's fitness is like a crystal ball for longevity! Let's check your cardiovascular health.",
    options: [
      { label: 'No, I get winded', value: 'winded', impact: 2.5, emoji: '😮‍💨' },
      { label: "Yes, but I'm breathing hard", value: 'breathing_hard', impact: 0, emoji: '😤' },
      { label: 'Yes, easily. I could do more!', value: 'easy', impact: -2.0, emoji: '💪' },
    ],
  },
  {
    id: 'stressLevel',
    title: 'The "Cortisol" Check',
    subtitle: 'Chronic Stress',
    question: 'How often do you feel overwhelmed or anxious to the point of physical tension?',
    mascotMessage: "Chronic stress literally ages your DNA! But awareness is the first step to managing it better.",
    options: [
      { label: 'Most days', value: 'most_days', impact: 3.0, emoji: '😰' },
      { label: 'Occasionally / Work deadlines', value: 'occasionally', impact: 0.5, emoji: '😅' },
      { label: 'Rarely / I manage stress well', value: 'rarely', impact: -1.0, emoji: '😌' },
    ],
  },
  {
    id: 'eatingWindow',
    title: 'The "Metabolic" Check',
    subtitle: 'Eating Window',
    question: 'When is your last meal relative to your bedtime?',
    mascotMessage: "Fasting time activates your cellular cleanup crew! Let's optimize your eating window.",
    options: [
      { label: 'Right before bed (< 1 hour)', value: 'before_bed', impact: 1.5, emoji: '🍕' },
      { label: '1-2 hours before', value: 'one_two_hours', impact: 0.5, emoji: '⏰' },
      { label: '3+ hours before (Fasted state)', value: 'fasted', impact: -1.0, emoji: '✨' },
    ],
  },
  {
    id: 'socialConnection',
    title: 'The "Blue Zone" Check',
    subtitle: 'Social Connection',
    question: 'How many meaningful social interactions (non-work) do you have weekly?',
    mascotMessage: "The world's longest-lived people share one secret: strong social bonds! Let's see where you stand.",
    options: [
      { label: '0-1 (Isolated)', value: 'isolated', impact: 2.0, emoji: '😔' },
      { label: '2-3 times', value: 'moderate', impact: 0, emoji: '🙂' },
      { label: '4+ (Strong community)', value: 'strong', impact: -1.5, emoji: '🎉' },
    ],
  },
  {
    id: 'functionalStrength',
    title: 'The "Grip Strength" Proxy',
    subtitle: 'Functional Strength',
    question: 'Can you hang from a pull-up bar (dead hang) for 30 seconds?',
    mascotMessage: "Grip strength is surprisingly connected to your overall health and lifespan! Let's test it!",
    options: [
      { label: 'No', value: 'no', impact: 1.0, emoji: '😓' },
      { label: 'Yes, barely', value: 'barely', impact: 0, emoji: '😤' },
      { label: 'Yes, easily', value: 'easily', impact: -1.5, emoji: '💪' },
    ],
  },
];

export default function Onboarding({ onComplete, onSkip }: Props) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    name: '',
    gender: '',
    age: '',
    country: '',
    weightKg: 70,
    heightCm: 170,
    waistCm: 80,
    sleepQuality: '',
    sugarIntake: '',
    cardioHealth: '',
    stressLevel: '',
    eatingWindow: '',
    socialConnection: '',
    functionalStrength: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [showCountries, setShowCountries] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // List of countries with flags
  const countriesWithFlags = [
    { name: 'Afghanistan', flag: '🇦🇫' }, { name: 'Albania', flag: '🇦🇱' }, { name: 'Algeria', flag: '🇩🇿' }, { name: 'Andorra', flag: '🇦🇩' },
    { name: 'Angola', flag: '🇦🇴' }, { name: 'Argentina', flag: '🇦🇷' }, { name: 'Armenia', flag: '🇦🇲' }, { name: 'Australia', flag: '🇦🇺' },
    { name: 'Austria', flag: '🇦🇹' }, { name: 'Azerbaijan', flag: '🇦🇿' }, { name: 'Bahamas', flag: '🇧🇸' }, { name: 'Bahrain', flag: '🇧🇭' },
    { name: 'Bangladesh', flag: '🇧🇩' }, { name: 'Barbados', flag: '🇧🇧' }, { name: 'Belarus', flag: '🇧🇾' }, { name: 'Belgium', flag: '🇧🇪' },
    { name: 'Belize', flag: '🇧🇿' }, { name: 'Benin', flag: '🇧🇯' }, { name: 'Bhutan', flag: '🇧🇹' }, { name: 'Bolivia', flag: '🇧🇴' },
    { name: 'Bosnia and Herzegovina', flag: '🇧🇦' }, { name: 'Botswana', flag: '🇧🇼' }, { name: 'Brazil', flag: '🇧🇷' }, { name: 'Brunei', flag: '🇧🇳' },
    { name: 'Bulgaria', flag: '🇧🇬' }, { name: 'Burkina Faso', flag: '🇧🇫' }, { name: 'Burundi', flag: '🇧🇮' }, { name: 'Cambodia', flag: '🇰🇭' },
    { name: 'Cameroon', flag: '🇨🇲' }, { name: 'Canada', flag: '🇨🇦' }, { name: 'Cape Verde', flag: '🇨🇻' }, { name: 'Central African Republic', flag: '🇨🇫' },
    { name: 'Chad', flag: '🇹🇩' }, { name: 'Chile', flag: '🇨🇱' }, { name: 'China', flag: '🇨🇳' }, { name: 'Colombia', flag: '🇨🇴' },
    { name: 'Comoros', flag: '🇰🇲' }, { name: 'Congo', flag: '🇨🇬' }, { name: 'Costa Rica', flag: '🇨🇷' }, { name: 'Croatia', flag: '🇭🇷' },
    { name: 'Cuba', flag: '🇨🇺' }, { name: 'Cyprus', flag: '🇨🇾' }, { name: 'Czech Republic', flag: '🇨🇿' }, { name: 'Denmark', flag: '🇩🇰' },
    { name: 'Djibouti', flag: '🇩🇯' }, { name: 'Dominica', flag: '🇩🇲' }, { name: 'Dominican Republic', flag: '🇩🇴' }, { name: 'Ecuador', flag: '🇪🇨' },
    { name: 'Egypt', flag: '🇪🇬' }, { name: 'El Salvador', flag: '🇸🇻' }, { name: 'Equatorial Guinea', flag: '🇬🇶' }, { name: 'Eritrea', flag: '🇪🇷' },
    { name: 'Estonia', flag: '🇪🇪' }, { name: 'Eswatini', flag: '🇸🇿' }, { name: 'Ethiopia', flag: '🇪🇹' }, { name: 'Fiji', flag: '🇫🇯' },
    { name: 'Finland', flag: '🇫🇮' }, { name: 'France', flag: '🇫🇷' }, { name: 'Gabon', flag: '🇬🇦' }, { name: 'Gambia', flag: '🇬🇲' },
    { name: 'Georgia', flag: '🇬🇪' }, { name: 'Germany', flag: '🇩🇪' }, { name: 'Ghana', flag: '🇬🇭' }, { name: 'Greece', flag: '🇬🇷' },
    { name: 'Grenada', flag: '🇬🇩' }, { name: 'Guatemala', flag: '🇬🇹' }, { name: 'Guinea', flag: '🇬🇳' }, { name: 'Guinea-Bissau', flag: '🇬🇼' },
    { name: 'Guyana', flag: '🇬🇾' }, { name: 'Haiti', flag: '🇭🇹' }, { name: 'Honduras', flag: '🇭🇳' }, { name: 'Hungary', flag: '🇭🇺' },
    { name: 'Iceland', flag: '🇮🇸' }, { name: 'India', flag: '🇮🇳' }, { name: 'Indonesia', flag: '🇮🇩' }, { name: 'Iran', flag: '🇮🇷' },
    { name: 'Iraq', flag: '🇮🇶' }, { name: 'Ireland', flag: '🇮🇪' }, { name: 'Israel', flag: '🇮🇱' }, { name: 'Italy', flag: '🇮🇹' },
    { name: 'Jamaica', flag: '🇯🇲' }, { name: 'Japan', flag: '🇯🇵' }, { name: 'Jordan', flag: '🇯🇴' }, { name: 'Kazakhstan', flag: '🇰🇿' },
    { name: 'Kenya', flag: '🇰🇪' }, { name: 'Kiribati', flag: '🇰🇮' }, { name: 'Kuwait', flag: '🇰🇼' }, { name: 'Kyrgyzstan', flag: '🇰🇬' },
    { name: 'Laos', flag: '🇱🇦' }, { name: 'Latvia', flag: '🇱🇻' }, { name: 'Lebanon', flag: '🇱🇧' }, { name: 'Lesotho', flag: '🇱🇸' },
    { name: 'Liberia', flag: '🇱🇷' }, { name: 'Libya', flag: '🇱🇾' }, { name: 'Liechtenstein', flag: '🇱🇮' }, { name: 'Lithuania', flag: '🇱🇹' },
    { name: 'Luxembourg', flag: '🇱🇺' }, { name: 'Madagascar', flag: '🇲🇬' }, { name: 'Malawi', flag: '🇲🇼' }, { name: 'Malaysia', flag: '🇲🇾' },
    { name: 'Maldives', flag: '🇲🇻' }, { name: 'Mali', flag: '🇲🇱' }, { name: 'Malta', flag: '🇲🇹' }, { name: 'Marshall Islands', flag: '🇲🇭' },
    { name: 'Mauritania', flag: '🇲🇷' }, { name: 'Mauritius', flag: '🇲🇺' }, { name: 'Mexico', flag: '🇲🇽' }, { name: 'Micronesia', flag: '🇫🇲' },
    { name: 'Moldova', flag: '🇲🇩' }, { name: 'Monaco', flag: '🇲🇨' }, { name: 'Mongolia', flag: '🇲🇳' }, { name: 'Montenegro', flag: '🇲🇪' },
    { name: 'Morocco', flag: '🇲🇦' }, { name: 'Mozambique', flag: '🇲🇿' }, { name: 'Myanmar', flag: '🇲🇲' }, { name: 'Namibia', flag: '🇳🇦' },
    { name: 'Nauru', flag: '🇳🇷' }, { name: 'Nepal', flag: '🇳🇵' }, { name: 'Netherlands', flag: '🇳🇱' }, { name: 'New Zealand', flag: '🇳🇿' },
    { name: 'Nicaragua', flag: '🇳🇮' }, { name: 'Niger', flag: '🇳🇪' }, { name: 'Nigeria', flag: '🇳🇬' }, { name: 'North Korea', flag: '🇰🇵' },
    { name: 'North Macedonia', flag: '🇲🇰' }, { name: 'Norway', flag: '🇳🇴' }, { name: 'Oman', flag: '🇴🇲' }, { name: 'Pakistan', flag: '🇵🇰' },
    { name: 'Palau', flag: '🇵🇼' }, { name: 'Palestine', flag: '🇵🇸' }, { name: 'Panama', flag: '🇵🇦' }, { name: 'Papua New Guinea', flag: '🇵🇬' },
    { name: 'Paraguay', flag: '🇵🇾' }, { name: 'Peru', flag: '🇵🇪' }, { name: 'Philippines', flag: '🇵🇭' }, { name: 'Poland', flag: '🇵🇱' },
    { name: 'Portugal', flag: '🇵🇹' }, { name: 'Qatar', flag: '🇶🇦' }, { name: 'Romania', flag: '🇷🇴' }, { name: 'Russia', flag: '🇷🇺' },
    { name: 'Rwanda', flag: '🇷🇼' }, { name: 'Saint Kitts and Nevis', flag: '🇰🇳' }, { name: 'Saint Lucia', flag: '🇱🇨' },
    { name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' }, { name: 'Samoa', flag: '🇼🇸' }, { name: 'San Marino', flag: '🇸🇲' },
    { name: 'Sao Tome and Principe', flag: '🇸🇹' }, { name: 'Saudi Arabia', flag: '🇸🇦' }, { name: 'Senegal', flag: '🇸🇳' },
    { name: 'Serbia', flag: '🇷🇸' }, { name: 'Seychelles', flag: '🇸🇨' }, { name: 'Sierra Leone', flag: '🇸🇱' }, { name: 'Singapore', flag: '🇸🇬' },
    { name: 'Slovakia', flag: '🇸🇰' }, { name: 'Slovenia', flag: '🇸🇮' }, { name: 'Solomon Islands', flag: '🇸🇧' }, { name: 'Somalia', flag: '🇸🇴' },
    { name: 'South Africa', flag: '🇿🇦' }, { name: 'South Korea', flag: '🇰🇷' }, { name: 'South Sudan', flag: '🇸🇸' }, { name: 'Spain', flag: '🇪🇸' },
    { name: 'Sri Lanka', flag: '🇱🇰' }, { name: 'Sudan', flag: '🇸🇩' }, { name: 'Suriname', flag: '🇸🇷' }, { name: 'Sweden', flag: '🇸🇪' },
    { name: 'Switzerland', flag: '🇨🇭' }, { name: 'Syria', flag: '🇸🇾' }, { name: 'Taiwan', flag: '🇹🇼' }, { name: 'Tajikistan', flag: '🇹🇯' },
    { name: 'Tanzania', flag: '🇹🇿' }, { name: 'Thailand', flag: '🇹🇭' }, { name: 'Timor-Leste', flag: '🇹🇱' }, { name: 'Togo', flag: '🇹🇬' },
    { name: 'Tonga', flag: '🇹🇴' }, { name: 'Trinidad and Tobago', flag: '🇹🇹' }, { name: 'Tunisia', flag: '🇹🇳' }, { name: 'Turkey', flag: '🇹🇷' },
    { name: 'Turkmenistan', flag: '🇹🇲' }, { name: 'Tuvalu', flag: '🇹🇻' }, { name: 'Uganda', flag: '🇺🇬' }, { name: 'Ukraine', flag: '🇺🇦' },
    { name: 'United Arab Emirates', flag: '🇦🇪' }, { name: 'United Kingdom', flag: '🇬🇧' }, { name: 'United States', flag: '🇺🇸' },
    { name: 'Uruguay', flag: '🇺🇾' }, { name: 'Uzbekistan', flag: '🇺🇿' }, { name: 'Vanuatu', flag: '🇻🇺' }, { name: 'Vatican City', flag: '🇻🇦' },
    { name: 'Venezuela', flag: '🇻🇪' }, { name: 'Vietnam', flag: '🇻🇳' }, { name: 'Yemen', flag: '🇾🇪' }, { name: 'Zambia', flag: '🇿🇲' },
    { name: 'Zimbabwe', flag: '🇿🇼' }
  ];

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      setShowExitDialog(true);
      // Push state back so user stays on page until they confirm
      window.history.pushState(null, '', window.location.pathname);
    };

    // Add initial history state
    window.history.pushState(null, '', window.location.pathname);
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleCountryChange = (value: string) => {
    setData({ ...data, country: value });
    
    if (value.trim().length > 0) {
      const filtered = countriesWithFlags.filter(country =>
        country.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setFilteredCountries(filtered.map(c => c.name));
      setShowCountries(true);
    } else {
      setShowCountries(false);
      setFilteredCountries([]);
    }
  };

  const selectCountry = (countryName: string) => {
    setData({ ...data, country: countryName });
    setShowCountries(false);
    setFilteredCountries([]);
  };

  const getCountryFlag = (countryName: string) => {
    const country = countriesWithFlags.find(c => c.name === countryName);
    return country ? country.flag : '🌍';
  };

  const calculateBioAge = () => {
    const chronologicalAge = parseInt(data.age);
    let biologicalAge = chronologicalAge;

    const heightMeters = data.heightCm / 100;
    const bmi = data.weightKg / (heightMeters * heightMeters);
    const whtr = data.waistCm / data.heightCm;

    if (bmi >= 30) {
      biologicalAge += 3.0;
    } else if (bmi >= 25) {
      biologicalAge += 1.5;
    } else if (bmi < 18.5) {
      biologicalAge += 1.0;
    }

    if (whtr > 0.6) {
      biologicalAge += 4.0;
    } else if (whtr > 0.5) {
      biologicalAge += 2.0;
    }

    questions.forEach((q) => {
      const answer = data[q.id as keyof OnboardingData];
      const option = q.options.find((opt) => opt.value === answer);
      if (option) {
        biologicalAge += option.impact;
      }
    });

    return biologicalAge.toFixed(1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const biologicalAge = calculateBioAge();

   await supabase.from('user_profiles').upsert({
  user_id: user.id,
  name: data.name,
  gender: data.gender,
  chronological_age: parseInt(data.age),
  country: data.country,
  weight_kg: data.weightKg,
  height_cm: data.heightCm,
  waist_cm: data.waistCm,
  biological_age: parseFloat(biologicalAge),
  onboarding_completed: true,
  sleep_quality: data.sleepQuality,
  sugar_intake: data.sugarIntake,
  cardio_health: data.cardioHealth,
  stress_level: data.stressLevel,
  eating_window: data.eatingWindow,
  social_connection: data.socialConnection,
  functional_strength: data.functionalStrength,
}, {
  onConflict: 'user_id'
});

      onComplete();
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipOnboarding = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Create minimal profile with onboarding_completed = false
      await supabase.from('user_profiles').upsert({
        user_id: user.id,
        name: data.name || 'User',
        onboarding_completed: false,
      });

      setShowExitDialog(false);
      
      // Always call the completion handler to navigate to dashboard
      if (onSkip) {
        onSkip();
      } else {
        onComplete();
      }
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (step < 7 + questions.length) {
      setDirection('forward');
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setDirection('backward');
      setStep(step - 1);
    } else {
      // On first screen, show exit dialog
      setShowExitDialog(true);
    }
  };

  const canProceed = () => {
    if (step === 0) return data.name.trim() !== '';
    if (step === 1) return data.gender !== '';
    if (step === 2) return data.age !== '' && parseInt(data.age) > 0;
    if (step === 3) return data.country.trim() !== '';
    if (step === 4) return data.weightKg > 0;
    if (step === 5) return data.heightCm > 0;
    if (step === 6) return data.waistCm > 0;
    if (step >= 7) {
      const questionIndex = step - 7;
      const questionId = questions[questionIndex]?.id;
      return data[questionId as keyof OnboardingData] !== '';
    }
    return false;
  };

  const totalSteps = 7 + questions.length + 1;
  const progressPercent = ((step + 1) / totalSteps) * 100;

  const renderBasicInfo = () => {
    if (step === 0) {
      return (
        <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="flex justify-center mb-2">
            <LongevityBot size={160} mode="listening" className="w-40 h-40 sm:w-48 sm:h-48" />
          </div>

          <div className="text-center px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">What's your name?</h2>
            <p className="text-sm sm:text-base text-gray-600">We'll tailor everything just for you</p>
          </div>

          <div className="px-4">
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              placeholder="Enter your name"
              className="w-full px-5 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:outline-none text-gray-900 text-lg font-medium transition-all"
              autoFocus
            />
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className={`space-y-8 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="text-center px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What's your gender?</h2>
            <p className="text-base text-gray-600">This helps us personalize your journey</p>
          </div>

          <div className="grid grid-cols-2 gap-3 px-4">
            {[
              { label: 'Male', emoji: '👨' },
              { label: 'Female', emoji: '👩' },
              { label: 'Non-binary', emoji: '🌈' },
              { label: 'Prefer not to say', emoji: '✨' }
            ].map((option) => (
              <button
                key={option.label}
                onClick={() => setData({ ...data, gender: option.label })}
                className={`p-6 rounded-2xl border-2 transition-all text-center ${
                  data.gender === option.label
                    ? 'border-blue-500 bg-blue-50 scale-105'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className="text-3xl mb-2">{option.emoji}</div>
                <div className="text-base font-semibold text-gray-900">{option.label}</div>
              </button>
            ))}
          </div>

          <div className="flex justify-center">
            <LongevityBot size={120} mode="listening" />
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="flex justify-center">
            <LongevityBot size={160} mode="listening" className="w-40 h-40 sm:w-48 sm:h-48" />
          </div>

          <div className="text-center px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">How old are you?</h2>
            <p className="text-sm sm:text-base text-gray-600">Your chronological age</p>
          </div>

          <div className="px-4">
            <input
              type="number"
              value={data.age}
              onChange={(e) => setData({ ...data, age: e.target.value })}
              placeholder="00"
              min="1"
              max="120"
              className="w-full px-5 py-5 sm:py-6 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:outline-none text-gray-900 text-center text-4xl sm:text-5xl font-bold transition-all"
              autoFocus
            />
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className={`space-y-8 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="text-center px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Where are you from?</h2>
            <p className="text-base text-gray-600">Your country</p>
          </div>

          <div className="px-4 relative">
            <input
              type="text"
              value={data.country}
              onChange={(e) => handleCountryChange(e.target.value)}
              onFocus={() => {
                if (data.country.trim().length > 0) {
                  const filtered = countriesWithFlags.filter(country =>
                    country.name.toLowerCase().includes(data.country.toLowerCase())
                  ).slice(0, 8);
                  setFilteredCountries(filtered.map(c => c.name));
                  setShowCountries(true);
                }
              }}
              onBlur={() => {
                // Delay to allow click on dropdown item
                setTimeout(() => setShowCountries(false), 200);
              }}
              placeholder="Start typing your country..."
              className="w-full px-5 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:outline-none text-gray-900 text-lg font-medium transition-all"
              autoFocus
            />
            
            {/* Country Dropdown */}
            {showCountries && filteredCountries.length > 0 && (
              <div className="absolute top-full left-4 right-4 mt-2 bg-white border-2 border-gray-200 shadow-xl max-h-64 overflow-y-auto z-50 rounded-2xl">
                {filteredCountries.map((country) => (
                  <button
                    key={country}
                    onClick={() => selectCountry(country)}
                    className={`w-full text-left px-5 py-4 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCountryFlag(country)}</span>
                      <span className="text-base font-semibold text-gray-900">{country}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <LongevityBot size={200} mode="listening" />
          </div>
        </div>
      );
    }
  };

  const renderBiometrics = () => {
    if (step === 4) {
      return (
        <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="flex justify-center mb-4">
            <LongevityBot size={140} mode="listening" className="w-32 h-32 sm:w-36 sm:h-36" />
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 mx-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              Weight is a key factor in calculating your BMI, which helps us understand your metabolic health and aging risk!
            </p>
          </div>

          <div className="px-4">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase tracking-wide mb-3">
              Body Composition
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What's your weight?</h2>

            <BiometricMeter
              value={data.weightKg}
              onChange={(value) => setData({ ...data, weightKg: value })}
              min={30}
              max={200}
              unit="kg"
              imperialUnit="lbs"
              toImperial={(kg) => kg * 2.20462}
              label="Weight"
              color="green"
            />
          </div>
        </div>
      );
    }

    if (step === 5) {
      return (
        <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="flex justify-center mb-4">
            <LongevityBot size={140} mode="listening" className="w-32 h-32 sm:w-36 sm:h-36" />
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 mx-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              Your height helps us calculate important ratios like BMI and waist-to-height ratio, both crucial for longevity assessment!
            </p>
          </div>

          <div className="px-4">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wide mb-3">
              Body Composition
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What's your height?</h2>

            <BiometricMeter
              value={data.heightCm}
              onChange={(value) => setData({ ...data, heightCm: value })}
              min={100}
              max={250}
              unit="cm"
              imperialUnit="in"
              toImperial={(cm) => cm / 2.54}
              label="Height"
              color="blue"
            />
          </div>
        </div>
      );
    }

    if (step === 6) {
      return (
        <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
          <div className="flex justify-center mb-4">
            <LongevityBot size={140} mode="listening" className="w-32 h-32 sm:w-36 sm:h-36" />
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 mx-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              Waist circumference reveals central adiposity and inflammation levels. Keeping your waist below half your height is a powerful longevity marker!
            </p>
          </div>

          <div className="px-4">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-xs font-bold uppercase tracking-wide mb-3">
              Metabolic Health
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What's your waist size?</h2>

            <BiometricMeter
              value={data.waistCm}
              onChange={(value) => setData({ ...data, waistCm: value })}
              min={50}
              max={200}
              unit="cm"
              imperialUnit="in"
              toImperial={(cm) => cm / 2.54}
              label="Waist"
              color="purple"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderQuestion = () => {
    const questionIndex = step - 7;
    if (questionIndex < 0 || questionIndex >= questions.length) return null;

    const question = questions[questionIndex];

    return (
      <div className={`space-y-6 ${direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
        <div className="flex justify-center mb-6">
          <LongevityBot size={120} mode="listening" />
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 mx-4">
          <p className="text-sm text-gray-700 leading-relaxed">{question.mascotMessage}</p>
        </div>

        <div className="px-4">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wide mb-3">
            {question.subtitle}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{question.question}</h2>
        </div>

        <div className="space-y-3 px-4">
          {question.options.map((option) => (
            <button
              key={option.value}
              onClick={() => setData({ ...data, [question.id]: option.value })}
              className={`w-full p-5 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
                data[question.id as keyof OnboardingData] === option.value
                  ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <span className="text-3xl flex-shrink-0">{option.emoji}</span>
              <span className="text-base font-semibold text-gray-900 flex-1">{option.label}</span>
              {data[question.id as keyof OnboardingData] === option.value && (
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                    <path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (step >= 7 + questions.length) {
    const bioAge = calculateBioAge();
    const chronoAge = parseInt(data.age);
    const difference = chronoAge - parseFloat(bioAge);
    const mascotMode = difference > 0 ? 'happy' : difference < 0 ? 'concern' : 'success';

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-2xl">
          <div className="flex justify-center mb-6">
            <LongevityBot size={200} mode={mascotMode} />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Biological Age</h2>
          <div className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-blue-600 mb-6">
            {bioAge}
          </div>

          {difference > 0 ? (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-6 mb-6">
              <div className="text-5xl mb-3">🎉</div>
              <p className="text-blue-800 font-bold text-lg mb-1">
                Amazing! You're {Math.abs(difference).toFixed(1)} years younger!
              </p>
              <p className="text-blue-700 text-sm">Your healthy habits are paying off!</p>
            </div>
          ) : difference < 0 ? (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-6 mb-6">
              <div className="text-5xl mb-3">💪</div>
              <p className="text-orange-800 font-bold text-lg mb-1">
                You're {Math.abs(difference).toFixed(1)} years older biologically
              </p>
              <p className="text-orange-700 text-sm">Don't worry, we'll help you reverse it!</p>
            </div>
          ) : (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-6 mb-6">
              <div className="text-5xl mb-3">⚖️</div>
              <p className="text-blue-800 font-bold text-lg">Perfectly balanced!</p>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-5 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : "START MY JOURNEY"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Onboarding Content */}
      <div className={`h-[100dvh] overflow-hidden bg-white flex flex-col ${showExitDialog ? 'pointer-events-none' : ''}`} style={{ height: '100dvh', minHeight: '-webkit-fill-available' }}>
        {/* Progress Bar - Duolingo style */}
        <div className="bg-white border-b border-gray-200 z-10 flex-shrink-0">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="w-10 h-10 rounded-2xl border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors flex-shrink-0"
              >
                <ChevronLeft size={20} strokeWidth={2.5} className="text-gray-600" />
              </button>
              <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 to-green-500 transition-all duration-500 rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-y-auto">
          <div className="max-w-lg w-full">
            {step < 4 ? renderBasicInfo() : step >= 4 && step <= 6 ? renderBiometrics() : renderQuestion()}
          </div>
        </div>

        {/* Bottom Button */}
        <div className="bg-white border-t border-gray-200 p-4 pb-safe flex-shrink-0 safe-area-inset-bottom">
          <div className="max-w-lg mx-auto">
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-2xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:bg-gray-300"
            >
              CONTINUE
            </button>
          </div>
        </div>

        <style>{`
          /* Prevent body scrolling */
          html, body {
            overflow: hidden;
            position: fixed;
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }

          /* Fix mobile viewport height */
          .mobile-viewport-fix {
            height: 100vh;
            height: 100dvh; /* Dynamic viewport height for mobile */
          }

          /* Safe area support for iOS and Android */
          .safe-area-inset-bottom {
            padding-bottom: max(1rem, env(safe-area-inset-bottom, 1rem));
          }

          .pb-safe {
            padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 1.5rem));
          }

          /* Ensure button is always visible */
          @supports (padding-bottom: env(safe-area-inset-bottom)) {
            .safe-area-inset-bottom {
              padding-bottom: calc(1rem + env(safe-area-inset-bottom));
            }
          }

          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .animate-slideInRight { animation: slideInRight 0.5s ease-out; }
          .animate-slideInLeft { animation: slideInLeft 0.5s ease-out; }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out;
          }

          @keyframes scaleIn {
            from { 
              opacity: 0;
              transform: scale(0.9);
            }
            to { 
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-scaleIn {
            animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(100%);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-slideUp {
            animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .pb-safe {
            padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
          }

          .touch-manipulation {
            touch-action: manipulation;
            -webkit-tap-highlight-color: transparent;
          }
        `}</style>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4 animate-fadeIn">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-slideUp sm:animate-scaleIn pb-safe">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-orange-100 flex items-center justify-center">
                <X className="text-orange-600" size={28} strokeWidth={2.5} />
              </div>
            </div>
            
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
              Skip Onboarding?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 leading-relaxed px-2">
              You can complete this later, but you won't be able to see your biological age until you do.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleSkipOnboarding}
                disabled={isLoading}
                className="w-full bg-gray-100 active:bg-gray-200 text-gray-900 py-4 sm:py-4 rounded-2xl font-bold text-sm sm:text-base transition-colors active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Go to Home'
                )}
              </button>
              <button
                onClick={() => setShowExitDialog(false)}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 sm:py-4 rounded-2xl font-bold text-sm sm:text-base shadow-lg active:shadow-xl transition-all active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue Onboarding
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}